// @ts-nocheck

/* eslint-disable react/no-danger */
import { CompactCard } from '@jo-v1/components';
import Dots from '@jo-v1/components/Loading/Dots';
import { PiiFormSection } from '@jo-v1/components/PiiFormSection';
import { validateInputs } from '@jo-v1/components/PiiFormSection/validateInputs';
import TcpaSection from '@jo-v1/features/TcpaSection';
import { useState, useEffect, useMemo, type Dispatch, type SetStateAction, type FC } from 'react';
import { HiChevronDoubleLeft } from 'react-icons/hi';
import { useDispatch, useSelector } from 'react-redux';

import useCheckScreen from '@core/blocks/eduResults/hooks/useCheckScreen';
import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import useElementEvents from '@core/hooks/cohesion/useElementEvents';
import { selectDcsDegrees } from '@core/reducers/dcsSlice';
import { selectAllInputs, selectInput, setInputs } from '@core/reducers/inputsSlice';
import { selectLeadDelivery, selectRecommenderMatches } from '@core/reducers/matchesSlice';
import { getSchoolConfig } from '@core/services/schoolConfig';
import cn from '@core/utils/classNames';
import { isUnderGraduate } from '@core/utils/isUnderGraduate';

import DecisionFactorsFlags from '../ProgramSelect/DecisionFactorsFlags';
import styles from './PiiConfirmation.module.css';

type Props = {
  setRecommenderStep: Dispatch<SetStateAction<string>>;
  piiLeadErrors: { field: string }[];
  editMode: boolean;
  setEditMode: Dispatch<SetStateAction<boolean>>;
};

const PiiConfirmation: FC<Props> = ({ setRecommenderStep, piiLeadErrors, editMode, setEditMode }) => {
  const { currentPrograms } = useSelector(selectLeadDelivery);
  const recommenderMatches = useSelector(selectRecommenderMatches);

  const { elementViewed, elementClicked } = useElementEvents({
    webElement: {
      elementType: 'card',
      location: recommenderMatches.length === 1 ? 'voyager-spotlight-pop-up' : 'voyager-recommender-pop-up',
      name: 'degree-oriented-audience',
      htmlId: 'jo-degree-oriented',
    },
  });

  useEffect(() => {
    elementViewed();
  }, []);

  const flags = useFeatureFlags();
  const startTime = useSelector((state) => selectInput(state, 'startTime'));
  const dispatch = useDispatch();
  // local state
  const [tcpa, setTcpa] = useState([]);
  const [loadingTcpa, setLoadingTcpa] = useState(true);

  const [degree] = useSelector(selectDcsDegrees);

  const isGrad = !isUnderGraduate(degree);

  const { isMobile } = useCheckScreen();

  const isMobileUndergrad = isMobile && !isGrad;

  const formInputs = useSelector(selectAllInputs);
  // First Name State
  const [firstName, setFirstName] = useState(formInputs.firstName?.value);
  // Last Name State
  const [lastName, setLastName] = useState(formInputs.lastName?.value);
  // Email State
  const [email, setEmail] = useState(formInputs.email?.value);
  // Phone State
  const [phone, setPhone] = useState(formInputs.phone?.value);

  const [hasPII, setHasPII] = useState<boolean>(true);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [formError, setFormError] = useState<{ field: string }[]>([]);
  const heading = 'Confirm your contact details:';

  useEffect(() => {
    if (!firstName || !lastName || !email || !phone) {
      setHasPII(false);
    }
    if (firstName && lastName && email && phone) {
      setHasPII(true);
    }
  }, [firstName, lastName, email, phone]);

  const handleSubmit = async () => {
    const formErrors = await validateInputs(firstName, lastName, email, phone);
    if (formErrors.length) {
      setFormError(formErrors);
      setIsSubmitting(false);
    }
    if (!formErrors.length) {
      if (hasPII) {
        dispatch(
          setInputs([
            { key: 'firstName', value: firstName },
            { key: 'lastName', value: lastName },
            { key: 'email', value: email },
            { key: 'phone', value: phone },
          ])
        );
      }
      setIsSubmitting(true);
      setRecommenderStep('SUBMISSIONS');
      elementClicked();
    }
  };
  // Build the TCPA data here to prevent the popup from jumping around after TCPA loads in
  const uniqueSchools = useMemo(
    () =>
      currentPrograms.reduce(
        (accumulator, current) =>
          // will only add the schoolId into the accumulator if it's not there already
          !accumulator.find((school) => school.schoolId === current.school.id)
            ? [...accumulator, { schoolId: current.school.id, hit: current }]
            : accumulator,
        [] // accumulator initial value
      ),
    [currentPrograms]
  );

  useEffect(() => {
    if (currentPrograms?.length === 0) {
      setRecommenderStep('PROGRAM_SELECT');
    }
  }, [currentPrograms?.length]);

  // useEffect for when uniqueSchoolIds is ready to loop through and call useSchoolConfig hook and store the data in tcpa state
  useEffect(() => {
    if (uniqueSchools.length > 0) {
      // build an array by looping through the uniqueSchoolIds and calling the async getSchoolConfig function
      const tcpaArray = uniqueSchools.map(async ({ schoolId, hit }) => {
        const schoolConfig = await getSchoolConfig(schoolId);
        return { ...schoolConfig, hit };
      });
      // once the array is built, set the tcpa state
      Promise.all(tcpaArray).then((data) => {
        setTcpa(data);
        setLoadingTcpa(false);
      });
    }
  }, [uniqueSchools]);

  const BackToTopMatches = (
    <button
      onClick={() => {
        setRecommenderStep('PROGRAM_SELECT');
      }}
      className={styles.backTopMatchesBtn}
      type="button"
    >
      <HiChevronDoubleLeft size={16} />
      Back to Top Matches
    </button>
  );

  const ctaText = 'Learn More';

  if (loadingTcpa)
    return (
      <section className={styles.loadingContainer}>
        <h2>Loading...</h2>
        <Dots />
      </section>
    );

  return (
    <form onSubmit={(e) => e.preventDefault()} data-tf-element-role="offer" className={styles.piiConfirmation}>
      <div className={styles.content}>
        <header>
          <h3>{`You’ve got matches${firstName ? `, ${firstName}` : ''}!`}</h3>
          <h4>These are your top recommendations based on:</h4>
          <DecisionFactorsFlags decisionFactors={formInputs['decision-factors']} />
          <div className={styles.selectedPrograms}>
            {currentPrograms?.map((hit) => (
              <CompactCard showClose={false} key={hit.program.id} hit={hit} />
            ))}
          </div>
        </header>
        <div className={styles.learnMorePiiWrapper}>
          <div className={styles.wantToLearnMore}>
            <h5>Want to learn more?</h5>
            <p>
              Submit your contact information to start a conversation with a school advisor. You can even start your
              application if you’re ready.
            </p>
          </div>

          <div className={styles.piiSection}>
            <div className={styles.panel}>
              <PiiFormSection
                program={currentPrograms[0]?.program}
                header={heading}
                editMode={editMode}
                piiLeadErrors={piiLeadErrors}
                setEditMode={setEditMode}
                location="recommender"
                firstName={firstName}
                setFirstName={setFirstName}
                lastName={lastName}
                setLastName={setLastName}
                email={email}
                setEmail={setEmail}
                phone={phone}
                setPhone={setPhone}
                formErrors={formError}
                setFormErrors={setFormError}
              />
            </div>
          </div>
        </div>

        <div className={styles.tcpa}>
          <TcpaSection tcpa={tcpa} />
          <div className={styles.buttonsWrapper}>
            {BackToTopMatches}
            <button
              className={cn(styles.submitInfoBtn, !hasPII && styles.disabledSubmitInfoBtn)}
              disabled={isSubmitting || !hasPII}
              id="reward_click_popup"
              data-testid="edumatch-leaddelivery-submit"
              onClick={handleSubmit}
              type="submit"
              data-tf-element-role="submit"
            >
              {ctaText}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default PiiConfirmation;
